import type { VoiceInputButtonRef } from "@agent-ui/features/chat/components/chat-elements/message-input/VoiceInputButton";
import type { DisplayMode } from "@agent-ui/features/modifiers/store/ModifiersStoreContext";
import type { Editor } from "@tiptap/react";
import type { ChatFileAttachment } from "@v2/features/chat/utils/chatFileAttachment";
import type { ModifiableElementType } from "schemas/generated/fileModifiers";

import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { ContextItemsMenu } from "@agent-ui/features/chat/components/chat-elements/context-items-menu/ContextItemsMenu";
import { FilePreview } from "@agent-ui/features/chat/components/chat-elements/message-input/FilePreview";
import { InspirationMenu } from "@agent-ui/features/chat/components/chat-elements/message-input/InspirationMenu";
import { MessageInputActionsMenu } from "@agent-ui/features/chat/components/chat-elements/message-input/MessageInputActionsMenu";
import { VoiceInputButton } from "@agent-ui/features/chat/components/chat-elements/message-input/VoiceInputButton";
import useChatMessageLimits from "@agent-ui/features/chat/hooks/useChatMessageLimits";
import { useCurrentChatContext } from "@agent-ui/features/chat/providers/CurrentChatContext";
import { useStreamingChats } from "@agent-ui/features/chat/store/ChatStoreContext";
import { showFileUploadDialog } from "@agent-ui/features/chat/utils/fileUpload";
import { useAutosizeTextArea } from "@agent-ui/features/chat/utils/use-autosize-textarea";
import ElementTypeTag from "@agent-ui/features/modifiers/ElementTypeTag";
import {
  useModifiersStoreActions,
  useSemanticElement,
} from "@agent-ui/features/modifiers/store/ModifiersStoreContext";
import { useNextDevServer } from "@agent-ui/features/page-renderer/components/code-execution/context";
import { Button } from "@replo/design-system/components/button/Button";
import {
  PopoverPrimitive,
  PopoverRoot,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { EditorContent } from "@tiptap/react";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { ArrowUp, AtSign, MousePointerClick, Square, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useSearchParams } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";
import { modifiableElementTypeSchema } from "schemas/fileModifiers";

interface MessageInputBaseProps {
  className?: string;
  disabled?: boolean;
  onStop?: () => void;
  enableInterrupt?: boolean;
  editor: Editor | null;
  isChatLoading: boolean;
  displayMode?: DisplayMode;
  toggleDisplayMode?: () => void;
  isIframeLoaded?: boolean;
  editorClassName?: string;
  variant?: "default" | "gradient";
  isMessageLimitShowing?: boolean;
  isViewingPreviousVersion?: boolean;
  /** Content to render between the + button and other actions (e.g., selected category chip) */
  leftEnhancer?: React.ReactNode;
}

interface MessageInputProps extends MessageInputBaseProps {
  allowAttachments: true;
  files: ChatFileAttachment[];
  setFiles: React.Dispatch<React.SetStateAction<ChatFileAttachment[]>>;
}

export interface MessageInputRef {
  triggerFileUpload: () => Promise<void>;
  triggerContextMenu: () => void;
  stopVoiceRecording: () => void;
  toggleVoiceRecording: () => void;
}

export const MessageInput = forwardRef<MessageInputRef, MessageInputProps>(
  function MessageInput(
    {
      className,
      onStop,
      enableInterrupt = true,
      disabled,
      editor,
      isChatLoading,
      displayMode,
      toggleDisplayMode,
      isIframeLoaded,
      editorClassName,
      variant = "default",
      isMessageLimitShowing = false,
      isViewingPreviousVersion = false,
      leftEnhancer,
      ...props
    },
    ref,
  ) {
    const logEvent = useLogAnalytics();
    const [searchParams] = useSearchParams();
    const pageId = searchParams.get("pageId");
    const chatId = searchParams.get("chatId");
    const [showInterruptPrompt, setShowInterruptPrompt] = useState(false);
    const streamingChats = useStreamingChats();
    const { hasReachedLimit: hasUserReachedMessageLimit } =
      useChatMessageLimits();
    const {
      isContextMenuOpen,
      dispatchCurrentChat,
      messagePopoverType,
      addFiles,
    } = useCurrentChatContext();
    const { isSyncPending } = useNextDevServer();

    const isCanvasReady = Boolean(isIframeLoaded) && !isSyncPending;

    const activeChats = streamingChats;
    const isLoading = isChatLoading;

    useOnValueChange(isLoading, (currentValue, previousValue) => {
      if (previousValue && !currentValue) {
        setShowInterruptPrompt(false);
      }
    });

    useImperativeHandle(
      ref,
      () => ({
        triggerFileUpload: async () => {
          if (props.allowAttachments) {
            const files = await showFileUploadDialog();
            await addFiles(files);
          }
        },
        triggerContextMenu: () => {
          // Use setTimeout to delay opening the context menu until after the editor update has processed
          setTimeout(() => {
            dispatchCurrentChat({ type: "openContextMenu" });
            if (editor) {
              editor.commands.focus();
            }
          }, 10);
        },
        stopVoiceRecording: () => {
          voiceInputButtonRef.current?.stopRecording();
        },
        toggleVoiceRecording: () => {
          voiceInputButtonRef.current?.toggleRecording();
        },
      }),
      [props.allowAttachments, dispatchCurrentChat, addFiles, editor],
    );

    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const voiceInputButtonRef = useRef<VoiceInputButtonRef | null>(null);

    const handleAtSignClick = () => {
      if (isContextMenuOpen) {
        dispatchCurrentChat({ type: "closeContextMenu" });
      } else {
        dispatchCurrentChat({ type: "openContextMenu" });
      }
    };

    const showFileList = props.allowAttachments && props.files.length > 0;

    useAutosizeTextArea({
      ref: textAreaRef,
      maxHeight: 240,
      borderWidth: 1,
      dependencies: [showFileList],
    });

    const containerClassName = twMerge(
      "relative w-full transition-[border] bg-white rounded-lg p-3 pr-3",
      variant === "gradient" && "rounded-3xl bg-none",
      !isMessageLimitShowing && "rounded-xl bg-white border border-border",
    );

    const containerStyle =
      variant === "gradient"
        ? {
            backgroundColor: "#FFFFFF",
            border: "8px solid rgba(255, 255, 255, 0.6)",
            backgroundClip: "padding-box",
          }
        : undefined;

    const isActiveChatsLimitReached = Boolean(
      activeChats.length > 5 && chatId && !activeChats.includes(chatId),
    );

    const getSubmitMessageTooltipText = () => {
      if (hasUserReachedMessageLimit) {
        return "You've used all your messages";
      }
      if (isActiveChatsLimitReached) {
        return "You can only have 5 requests running at a time";
      }
      return undefined;
    };

    const handleOpenChange = (open: boolean) => {
      if (!open) {
        if (messagePopoverType === "contextMenu") {
          dispatchCurrentChat({ type: "closeContextMenu" });
        }
        if (messagePopoverType === "inspiration") {
          dispatchCurrentChat({ type: "closeInspirationMenu" });
        }
      }
    };

    const popoverContent = messagePopoverType
      ? exhaustiveSwitch({ type: messagePopoverType })({
          inspiration: (
            <InspirationMenu
              editor={editor}
              onClose={() => {
                return dispatchCurrentChat({ type: "closeInspirationMenu" });
              }}
            />
          ),
          contextMenu: (
            <ContextItemsMenu
              editor={editor}
              onClose={() => dispatchCurrentChat({ type: "closeContextMenu" })}
            />
          ),
        })
      : null;

    return (
      <PopoverRoot
        open={Boolean(messagePopoverType)}
        onOpenChange={handleOpenChange}
      >
        <PopoverTrigger render={<div className="w-full" />}>
          <div
            ref={containerRef}
            className={containerClassName}
            style={containerStyle}
            onClick={() => {
              editor?.commands.focus();
            }}
          >
            {enableInterrupt && (
              <InterruptPrompt
                isOpen={showInterruptPrompt}
                close={() => setShowInterruptPrompt(false)}
              />
            )}
            {pageId && <SelectedElementIndicator />}
            {props.allowAttachments && props.files.length > 0 && (
              <div className="z-20 overflow-x-auto py-2 pb-3">
                <div className="flex space-x-3">
                  <AnimatePresence mode="popLayout">
                    {props.files.map((attachment) => {
                      return (
                        <FilePreview
                          key={attachment.id}
                          file={attachment}
                          onRemove={() => {
                            props.setFiles((files) => {
                              return files.filter(
                                (f) => f.id !== attachment.id,
                              );
                            });
                          }}
                        />
                      );
                    })}
                  </AnimatePresence>
                </div>
              </div>
            )}

            <EditorContent
              className={twMerge(
                "w-full pb-3 overflow-y-auto",
                editorClassName,
              )}
              editor={editor}
            />

            <div className="z-20 flex flex-row justify-between gap-2 w-full pt-2">
              <div className="flex gap-2">
                <Button
                  icon
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAtSignClick();
                  }}
                  id="at-sign-button"
                >
                  <AtSign />
                </Button>

                <MessageInputActionsMenu
                  handleAttachFiles={() => {
                    void showFileUploadDialog().then((files) => {
                      void addFiles(files);
                    });
                  }}
                />

                {leftEnhancer}

                {/*
                NOTE (Kurt, 2025-08-06): The <PageRenderer> component only shows up in the editor when
                there is a pageId in the URL. This ensures that the select button only shows up in the
                message input when there is a page available.
                */}
                {pageId && toggleDisplayMode && Boolean(displayMode) && (
                  <ConditionalTooltip
                    content={
                      isViewingPreviousVersion
                        ? "Cannot edit while viewing a previous version"
                        : undefined
                    }
                  >
                    <Button
                      size="base"
                      variant="secondary"
                      disabled={!isCanvasReady || isViewingPreviousVersion}
                      UNSAFE_className={twMerge(
                        displayMode === "select" &&
                          "bg-sky-soft border border-sky-border hover:bg-sky-soft text-sky hover:text-sky",
                      )}
                      startEnhancer={
                        <MousePointerClick
                          className={twMerge(
                            displayMode === "select" && "text-primary",
                          )}
                        />
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDisplayMode();
                        if (displayMode !== "select") {
                          logEvent("editor.modifier.enable");
                        }
                      }}
                    >
                      Edit
                    </Button>
                  </ConditionalTooltip>
                )}
              </div>

              <div className="flex gap-2">
                <VoiceInputButton ref={voiceInputButtonRef} editor={editor} />

                {isLoading ? (
                  <Button
                    icon
                    variant="secondary"
                    size="base"
                    aria-label="Stop generating"
                    onClick={onStop}
                  >
                    <Square className="animate-pulse" fill="currentColor" />
                  </Button>
                ) : (
                  <ConditionalTooltip content={getSubmitMessageTooltipText()}>
                    {/* NOTE: Using 'as any' because base-ui Button type definition has a discriminated union
                        that excludes 'type' prop unless nativeButton is true, but the prop is passed through
                        at runtime via spread */}
                    <Button
                      icon
                      {...({ type: "submit" } as any)}
                      size="base"
                      variant="primary"
                      aria-label="Send message"
                      disabled={
                        hasUserReachedMessageLimit || isActiveChatsLimitReached
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <ArrowUp />
                    </Button>
                  </ConditionalTooltip>
                )}
              </div>
            </div>
          </div>
        </PopoverTrigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Positioner
            side="top"
            sideOffset={4}
            collisionPadding={60}
            collisionAvoidance={{ side: "none", align: "shift" }}
            className="isolate z-[100]"
          >
            <PopoverPrimitive.Popup
              className="p-0 border-0.5 border-border overflow-hidden bg-white rounded-xl shadow-md outline-hidden origin-(--transform-origin) data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
              style={{
                width: "var(--anchor-width)",
                height:
                  // NOTE (Max, 2025-09-29): We only want the popover to be the full available height for the inspiration menu
                  messagePopoverType === "inspiration"
                    ? "var(--available-height)"
                    : undefined,
              }}
            >
              {popoverContent}
            </PopoverPrimitive.Popup>
          </PopoverPrimitive.Positioner>
        </PopoverPrimitive.Portal>
      </PopoverRoot>
    );
  },
);

interface InterruptPromptProps {
  isOpen: boolean;
  close: () => void;
}

function InterruptPrompt({ isOpen, close }: InterruptPromptProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ top: 0, filter: "blur(5px)" }}
          animate={{
            top: -40,
            filter: "blur(0px)",
            transition: {
              type: "spring",
              filter: { type: "tween" },
            },
          }}
          exit={{ top: 0, filter: "blur(5px)" }}
          className="absolute left-1/2 flex -translate-x-1/2 overflow-hidden whitespace-nowrap rounded-full border bg-background py-1 text-center text-xs text-muted-foreground"
        >
          <span className="ml-2.5">Press Enter again to interrupt</span>
          <button
            className="ml-1 mr-2.5 flex items-center"
            type="button"
            onClick={close}
            aria-label="Close"
          >
            <X className="h-3 w-3" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ConditionalTooltip({
  content,
  children,
}: React.PropsWithChildren<{
  content: string | undefined;
}>) {
  if (!content) {
    return <>{children}</>;
  }
  return <Tooltip content={content}>{children}</Tooltip>;
}

function toModifiableElementType(value: string): ModifiableElementType {
  const parsed = modifiableElementTypeSchema.safeParse(value);
  return parsed.success ? parsed.data : "unknown";
}

// NOTE (Kevin, 2025-08-11): Selected element indicator shown above the editor input when a selection exists.
// Displays element type and a clear button to unselect the element.
function SelectedElementIndicator() {
  const semanticElement = useSemanticElement();
  const modifierActions = useModifiersStoreActions();

  if (!semanticElement) {
    return null;
  }

  return (
    <div className="mb-2 -mt-1 inline-flex items-center gap-1">
      <div className="cursor-default">
        <ElementTypeTag
          elementType={toModifiableElementType(semanticElement.elementType)}
          showRemoveOnHover
          onRemove={() => modifierActions.clearSemanticSelection()}
        />
      </div>
    </div>
  );
}
